import { AppstoreOutlined, CopyOutlined, MessageOutlined, ShareAltOutlined } from '@ant-design/icons';
import { Button, Tooltip, message } from 'antd';
import React from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { extractAppUrls } from '../../../utils/appUrlUtils';

const PostActions = ({ post, isDarkMode, compact = false }) => {
  const { isAdmin } = useAuth();
  // Get unique app URLs from content
  const appUrlData = extractAppUrls(post.content);
  const isAnonymous = post.is_shared_anonymously;

  const handleViewApp = (appUrl) => {
    window.open(appUrl, '_blank');
  };

  const handleViewOriginalChat = () => {
    if (post.chat_id && !isAnonymous) {
      window.open(`/chat/${post.chat_id}`, '_blank');
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/posts/${post.id}`;
    const shareData = {
      title: post.title,
      url: shareUrl
    };
    
    try {
      // Modern browsers: Web Share API
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      
      // Fallback: Simple clipboard copy
      await navigator.clipboard.writeText(shareUrl);
      message.success('Link copied!');
      
    } catch (error) {
      // Legacy fallback (rare case)
      try {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        message.success('Link copied!');
      } catch (fallbackError) {
        message.error('Unable to share');
      }
    }
  };

  const handleCopyPostId = async () => {
    try {
      await navigator.clipboard.writeText(post.id);
      message.success('Post ID copied!');
    } catch (error) {
      // Legacy fallback (rare case)
      try {
        const textArea = document.createElement('textarea');
        textArea.value = post.id;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        message.success('Post ID copied!');
      } catch (fallbackError) {
        message.error('Unable to copy Post ID');
      }
    }
  };

  // In compact mode, only show if there are app URLs
  if (compact) {
    if (appUrlData.length === 0) return null;

    return (
      <div className="post-actions">
        <div className="actions-left" />
        <div className="actions-right">
          {appUrlData.map((appData, index) => {
            const pathParts = appData.path ? appData.path.split('/').filter(part => part.length > 0) : [];
            const hasCustomPath = pathParts.length > 1;
            
            let buttonLabel;
            if (hasCustomPath) {
              const filename = pathParts[pathParts.length - 1];
              buttonLabel = `View App (${filename})`;
            } else {
              buttonLabel = `View App${appUrlData.length > 1 ? ` #${index + 1}` : ''}`;
            }
            
            return (
              <Button
                key={appData.url}
                type={index === 0 ? "primary" : "default"}
                size="small"
                icon={<AppstoreOutlined />}
                onClick={() => handleViewApp(appData.url)}
                style={{ marginLeft: index > 0 ? 8 : 0 }}
              >
                {buttonLabel}
              </Button>
            );
          })}
        </div>
      </div>
    );
  }

  // Don't render actions if no actions are available.
  // Admins always get the bar so the Copy Post ID button is reachable.
  const hasActions = appUrlData.length > 0 || post.chat_id || isAdmin;
  if (!hasActions) {
    return null;
  }

  return (
    <div className="post-actions">
      <div className="actions-left">
        {/* Chat button - disabled for anonymous posts */}
        {post.chat_id && (
          <Tooltip
            title={isAnonymous ? "Chat not available for anonymous posts" : "View original chat conversation"}
            placement="top"
          >
            <Button
              type="text"
              size="small"
              icon={<MessageOutlined />}
              onClick={handleViewOriginalChat}
              disabled={isAnonymous}
            >
              View Chat
            </Button>
          </Tooltip>
        )}

        {/* Share button - always visible */}
        <Button
          type="text"
          size="small"
          icon={<ShareAltOutlined />}
          onClick={handleShare}
        >
          Share
        </Button>

        {/* Copy Post ID - admin only */}
        {isAdmin && (
          <Tooltip title="Copy this post's ID" placement="top">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={handleCopyPostId}
            >
              Copy Post ID
            </Button>
          </Tooltip>
        )}
      </div>

      {/* App buttons - show all unique apps with proper labels */}
      {appUrlData.length > 0 && (
        <div className="actions-right">
          {appUrlData.map((appData, index) => {
            // Generate button label based on path
            // Split path and filter out empty parts (handles trailing slashes)
            const pathParts = appData.path ? appData.path.split('/').filter(part => part.length > 0) : [];
            const hasCustomPath = pathParts.length > 1; // More than just the app ID
            
            let buttonLabel;
            if (hasCustomPath) {
              // Get the last non-empty part of the path
              const filename = pathParts[pathParts.length - 1];
              buttonLabel = `View App (${filename})`;
            } else {
              // Simple app URL or multiple apps
              buttonLabel = `View App${appUrlData.length > 1 ? ` #${index + 1}` : ''}`;
            }
            
            return (
              <Button
                key={appData.url}
                type={index === 0 ? "primary" : "default"}
                size="small"
                icon={<AppstoreOutlined />}
                onClick={() => handleViewApp(appData.url)}
                style={{ marginLeft: index > 0 ? 8 : 0 }}
              >
                {buttonLabel}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PostActions;
